import { apiGetCasino, apiGetCasinoFeed, apiGetOffers, apiGetMore } from '@/api';
import * as types from '@/store/mutation-types';

// Axios cancel request
import axios from '@/axios.config';
let oldSearchToken = null;

// Models
import CASINO from '@/store/models/casino';
import PAGINATION from '@/store/models/pagination';

// Services
import { mapEnabled} from '@/store/services/mapEnabled';
import { mapEnabledToSlug } from '@/store/services/mapEnabledToSlug';
import { mapEnabledToKey } from '@/store/services/mapEnabledToKey';
import { setEnabled } from '@/store/services/setEnabled';

const defaultState = () => {
  return {
    profile: CASINO,

    feed: {
      data: [],
      meta: PAGINATION,
      loading: null
    },

    search: {
      data: [],
      meta: PAGINATION,
      loading: null
    },

    filters: {
      categories: [
        {
          label: 'Everything',
          slug: 'all',
          icon: 'planet',
          enabled: true
        },
        {
          label: 'Games',
          slug: 'game',
          icon: 'ghost',
          enabled: false
        },
        {
          label: 'Offers',
          slug: 'offer',
          icon: 'gift',
          enabled: false
        },
        {
          label: 'Updates',
          slug: 'status',
          icon: 'crown',
          enabled: false
        }
      ],
      gameTypes: [],
      view: 'card-sm'
    }
  }
}

// initial state
const state = defaultState();

// mutations
const mutations = {
  [types.RECEIVED_CASINO] (state, casino) {
    state.profile = casino;
  },

  [types.RECEIVED_CASINO_FEED] (state, feed) {
    state.feed.data = feed.data;
    state.feed.meta = feed.meta;
  },

  [types.RECEIVED_CASINO_GAMETYPES] (state, gameTypes) {
    state.filters.gameTypes = gameTypes;
  },

  [types.RECEIVED_CASINO_FEED_SEARCH] (state, search) {
    state.search.data = search.data;
    state.search.meta = search.meta;
  },

  [types.APPEND_CASINO_FEED] (state, feed) {
    state.feed.data = state.feed.data.concat(feed.data);
    state.feed.meta = feed.meta;
  },

  [types.APPEND_CASINO_FEED_SEARCH] (state, search) {
    state.search.data = state.search.data.concat(search.data);
    state.search.meta = search.meta;
  },

  [types.UPDATE_CASINO_CATEGORIES] (state, category) {
    // Disable all filter buttons so only one can ever be active
    state.filters.categories.forEach(el => el.enabled = false);

    // Find matching object with slug and update enabled state
    setEnabled(state.filters.categories, category);
  },

  [types.UPDATE_CASINO_GAMETYPES] (state, gameType) {
    // Find matching object with slug and update enabled state
    setEnabled(state.filters.gameTypes, gameType);
  },

  [types.SET_CASINO_VIEW] (state, view) {
    state.filters.view = view;
  },

  [types.SET_LOADING_FEED] (state, prom) {
    state.feed.loading = prom;
  },

  [types.SET_LOADING_FEED_SEARCH] (state, prom) {
    state.search.loading = prom;
  },

  [types.RESET_CASINO_GAMETYPE_FILTERS] (state) {
    state.filters.gameTypes.forEach(el => el.enabled = false);
  },

  [types.RESET_CASINO] (state) {
    Object.assign(state, defaultState());
  },
};

const actions = {
  getCasino: ({ commit }, { slug, args }) => {
    return apiGetCasino(slug, args)
      .then(({ data }) => {
        commit(types.RECEIVED_CASINO, data);
      })
      .catch((error) => {
        console.log(error);
      });
  },

  getCasinoFeed: ({ commit, state }, { slug, args = {} }) => {
    // Argument defaults
    args.limit = args.limit || 16;

    // Because we are within the store we need to
    // use Vue.prototype to access the $mq plugin
    if (Vue.prototype.$mq('sm-max')) { 
      args.is_mobile_friendly = true; 
    };

    let feedTypes = [];

    if (!state.filters.categories[0].enabled) {
      feedTypes = mapEnabledToKey(state.filters.categories, 'type');
    }

    let gameTypes = mapEnabledToKey(state.filters.gameTypes);

    let fetchFeed = apiGetCasinoFeed(slug, { types: feedTypes, game_types: gameTypes, ...args })
      .then(({ data }) => {
        commit(types.RECEIVED_CASINO_FEED, data);
      })
      .catch((error) => {
        console.log(error);
      });

    commit(types.SET_LOADING_FEED, fetchFeed);

    return fetchFeed;
  },

  getMoreCasinoFeed: ({ commit }, params) => {
    return apiGetMore(params.url)
      .then(({ data }) => {
        commit(types[params.commit], data);
      })
      .catch((error) => {
        console.log(error);
    });
  },

  searchCasinoFeed: ({ commit, state }, { slug, term, args = {} }) => {
    // Argument defaults
    args.limit = args.limit || 16;

    if (Vue.prototype.$mq('sm-max')) { 
      args.is_mobile_friendly = true; 
    };

    // Setup a cancel token so we can cancel any unresolved previous searches
    let token = axios.CancelToken;
    let request = token.source();

    if (oldSearchToken) {
      oldSearchToken.cancel();
      oldSearchToken = null;
    }

    oldSearchToken = request;

    let fetchFeed = apiGetCasinoFeed(slug, { search: term, ...args }, request.token)
      .then(({ data }) => {
        commit(types.RECEIVED_CASINO_FEED_SEARCH, data);
        oldSearchToken = null;
      })
      .catch((error) => {
        console.log(error);
      });

    commit(types.SET_LOADING_FEED_SEARCH, fetchFeed);

    return fetchFeed;
  },

  setCasinoFilter: ({ commit, dispatch, state, getters }, category) => {
    commit(types.UPDATE_CASINO_CATEGORIES, category);

    if (getters.casinoGameTypesActive) commit(types.RESET_CASINO_GAMETYPE_FILTERS);;

    dispatch('getCasinoFeed', { slug: state.profile.slug });
  },

  setCasinoView: ({ commit }, view) => {
    commit(types.SET_CASINO_VIEW, view);
  },

  toggleCasinoGameTypeFilter: ({ commit, dispatch, state }, gameType) => {
    commit(types.UPDATE_CASINO_GAMETYPES, gameType);

    dispatch('getCasinoFeed', { slug: state.profile.slug });
  },

  clearCasinoGameTypeFilters: ({ commit, dispatch }) => {
    commit(types.RESET_CASINO_GAMETYPE_FILTERS);

    dispatch('getCasinoFeed', { slug: state.profile.slug });
  }
};

const getters = {
  casino (state) {
    return state.profile;
  },

  casinoFeaturedOffer (state) {
    return state.profile.OfferCarouselNotExpired;
  },

  casinoMostPopularGames (state) {
    return state.profile.manualMostPopularGames.data;
  },

  casinoFeed (state) {
    return state.feed;
  },

  casinoSearch (state) {
    return state.search;
  },

  casinoFilters (state) {
    return state.filters;
  },

  casinoGameTypesActive (state) {
    let activeCategory = state.filters.categories.find(item => item.enabled);

    return ['all', 'status'].includes(activeCategory.slug);
  },

  casinoGameTypeFiltersCount (state) {
    return state.filters.gameTypes.filter(item => item.enabled).length;
  },
};

export default {
  state,
  mutations,
  actions,
  getters
};



// WEBPACK FOOTER //
// ./resources/assets/js/app/store/modules/casino.js